import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useDocuments } from '@pages/Profile/DocumentManagement/DocumentManagementPage'

import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { isFileDuplicated, removeUploadedFile } from '@ui/components/FileUpload/FileUpload'
import { FileData } from '@ui/components/FileUpload/types'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import { CreateClientDocument } from '@model/CreateClientDocuments'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryDto } from '@model/DocumentDto'
import { UploadDocumentTypeEnum } from '@model/UploadDocumentTypeEnum'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { DropdownInput } from '@shared/components/Dropdown'
import { Loading } from '@shared/components/Loading/Loading'
import { ConfirmModal } from '@shared/modals/Confirm.modal'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from './DocumentsInfoPage.module.scss'

export interface DocumentInfoPageProofOfAddressStepFormValues {
  documentType: { id: UploadDocumentTypeEnum; name: string }
  documentTypes?: DocumentCategoryDto
  files: FileData[]
  documents: DocumentManagementDocument[]
}

interface DocumentManagementDocument {
  categoryId: number
  typeId: number
  file: string
  filename: string
  error?: boolean
}

const DocumentInfoPageProofOfAddressStepFormUI: React.FC<
  FormikProps<DocumentInfoPageProofOfAddressStepFormValues> & OuterProps
> = (props) => {
  useScrollToTop()
  const { documentTypes, onSkip, handleSubmit, isSubmitting, onSkipDocuments } = props
  const { t } = useTranslation()

  const handleSubmitForm: FormEventHandler = (event) => {
    event.preventDefault()
    handleSubmit()
  }

  return (
    <Form onSubmit={handleSubmitForm}>
      {!isSubmitting ? (
        <FormFieldsFactory
          documentTypes={documentTypes}
          onSkipDocuments={onSkipDocuments}
          onCancel={onSkip}
        />
      ) : (
        <Loading
          isLoading={isSubmitting}
          showLoadingIcon
          text={t('Sign up.Submitting Proof of Address...')}
        />
      )}
    </Form>
  )
}

interface FormFieldsFactoryProps {
  documentTypes?: DocumentCategoryDto

  onSkipDocuments(): void
  onCancel(): void
}

const FormFieldsFactory: React.FC<FormFieldsFactoryProps> = (props) => {
  const { onSkipDocuments } = props
  const { t } = useTranslation()
  const { values, isValid, setValues } =
    useFormikContext<DocumentInfoPageProofOfAddressStepFormValues>()

  const duplicateInfoModal = useModal(InformationModal, {
    title: t('Upload failed'),
    secondaryLabel: t('Got It'),
    description: () => <Body1>{t('errors.Document is already uploaded!')}</Body1>,
  })
  const [file, setFile] = useState<FileData>()
  const deleteFileConfirmModal = useModal(ConfirmModal, {
    title: t('Sign up.Remove Document'),
    description: t('Sign up.Do you really want to delete this document?'),
    primaryLabel: t('Yes'),
    secondaryLabel: t('Cancel'),
  })
  const skipUploadConfirmModal = useModal(ConfirmModal, {
    title: t('Sign up.Skip document upload?'),
    description: t(
      'Sign up.You can skip document upload, but uploading documents is required step to become Tickmill customer.'
    ),
    primaryLabel: t('Sign up.Skip upload'),
    secondaryLabel: t('Sign up.Back to Upload'),
  })

  const entity = useSessionEntity()
  const documentsProps = useDocuments()

  useEffect(() => {
    setValues({
      ...values,
      documents: [],
      files: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.documentType])

  const handleDocumentUploads = (filesData: FileData[], currentFile?: FileData) => {
    if (
      currentFile &&
      isFileDuplicated(
        documentsProps.data,
        filesData,
        currentFile,
        false,
        false,
        DocumentCategoryType.Address
      )
    ) {
      const newDocuments = removeUploadedFile(filesData, currentFile)
      duplicateInfoModal.open()
      setValues({
        ...values,
        files: newDocuments,
        documents: newDocuments.map((x) => ({
          ...x,
          categoryId: DocumentCategoryType.Address,
          typeId: Number.parseInt(values.documentType.id.toString(), 10),
          filename: x.fileName,
          file: x.base64Content,
        })),
      })
    } else {
      setValues({
        ...values,
        files: filesData,
        documents: filesData.map((x) => ({
          ...x,
          categoryId: DocumentCategoryType.Address,
          typeId: Number.parseInt(values.documentType.id.toString(), 10),
          filename: x.fileName,
          file: x.base64Content,
        })),
      })
    }
  }

  const handleDocumentBeforeRemove = (file: FileData) => {
    setFile(file)
    deleteFileConfirmModal.open({ onConfirm: handleDocumentRemove })
  }

  const handleDocumentRemove = () => {
    const files = removeUploadedFile(values.files, file)
    setValues({
      ...values,
      files,
      documents: files.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Address,
        typeId: Number.parseInt(values.documentType.id.toString(), 10),
        filename: x.fileName,
        file: x.base64Content,
      })),
    })
  }

  const getEntityInfoText = () => {
    if (entity === TickmillCompaniesEnum.TICKMILL_UK) {
      return t('Profile.Proof of address must be no more than', {
        length: 90,
      })
    } else if (entity === TickmillCompaniesEnum.TICKMILL_EU) {
      return t('Profile.Proof of address must be no more than', {
        length: 180,
      })
    }

    return t('Profile.Proof of address must be no more than', {
      length: 180,
    })
  }

  return (
    <React.Fragment>
      <Heading6 medium>{t('Sign up.Proof Of Address')} * </Heading6>
      <Body1 isParagraph className='mb-5'>
        {getEntityInfoText()}
      </Body1>
      <DropdownInput
        label={t('Sign up.Document Type')}
        value={values.documentType.id}
        name='documentType.name'
        required
        options={values.documentTypes?.types}
        onChange={(value) =>
          setValues({
            ...values,
            documentType: value,
          })
        }
      />
      {!!values.documentType.id && (
        <div className='pb-3'>
          <DocumentFileUpload
            documentCategory={DocumentCategoryType.Address}
            files={values.files}
            onUpload={handleDocumentUploads}
            onBeforeRemove={handleDocumentBeforeRemove}
            multiple
          />
        </div>
      )}
      <div className='is-flex is-justify-content-center'>
        <div className={styles.button}>
          <Button.Secondary
            size='lg'
            fullWidth
            onClick={() => skipUploadConfirmModal.open({ onConfirm: onSkipDocuments })}
          >
            {t('Skip')}
          </Button.Secondary>
          <Button.Primary size='lg' type='submit' fullWidth disabled={!isValid}>
            {t('Submit')}
          </Button.Primary>
        </div>
      </div>
    </React.Fragment>
  )
}

interface OuterProps {
  documentTypes?: DocumentCategoryDto

  onSkip(): void
  onSkipDocuments(): void

  handleSubmit(documents: CreateClientDocument[]): Promise<void>
}

export const DocumentInfoPageProofOfAddressStepForm = withFormik<
  OuterProps,
  DocumentInfoPageProofOfAddressStepFormValues
>({
  mapPropsToValues: (props) => {
    return {
      files: [],
      documentType: { id: UploadDocumentTypeEnum.None, name: '' },
      documentTypes: props.documentTypes,
      documents: [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.handleSubmit(values.documents)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<DocumentInfoPageProofOfAddressStepFormValues> = {}

    const documents = values.documents.filter((x) => !!x?.filename).filter((x) => !x.error)
    if (documents?.length <= 0) {
      errors.documents = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(DocumentInfoPageProofOfAddressStepFormUI)
